import { useEffect } from 'react';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
  hreflangs?: { lang: string; href: string }[];
  breadcrumbs?: BreadcrumbItem[];
  faq?: FAQItem[];
}

export function useSEO({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  jsonLd,
  hreflangs,
  breadcrumbs,
  faq,
}: SEOHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDesc) metaDesc.content = description;

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.rel = 'canonical';
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.href = canonical;
    }

    // OG tags
    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const setTwitterMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('og:title', ogTitle || title);
    setMeta('og:description', ogDescription || description);
    setMeta('og:type', ogType);
    const resolvedUrl = canonical || (typeof window !== 'undefined' ? window.location.href : undefined);
    if (resolvedUrl) setMeta('og:url', resolvedUrl);
    setTwitterMeta('twitter:title', ogTitle || title);
    setTwitterMeta('twitter:description', ogDescription || description);

    // Hreflang tags
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    if (hreflangs && hreflangs.length > 0) {
      hreflangs.forEach(({ lang, href }) => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = lang;
        link.href = href;
        link.setAttribute('data-hreflang', 'true');
        document.head.appendChild(link);
      });
    }

    // Page JSON-LD
    let scriptEl = document.getElementById('page-jsonld') as HTMLScriptElement;
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'page-jsonld';
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    }

    // BreadcrumbList JSON-LD
    let breadcrumbEl = document.getElementById('breadcrumb-jsonld') as HTMLScriptElement;
    if (breadcrumbs && breadcrumbs.length > 0) {
      if (!breadcrumbEl) {
        breadcrumbEl = document.createElement('script');
        breadcrumbEl.id = 'breadcrumb-jsonld';
        breadcrumbEl.type = 'application/ld+json';
        document.head.appendChild(breadcrumbEl);
      }
      breadcrumbEl.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": item.name,
          "item": item.url,
        })),
      });
    }

    // FAQ JSON-LD
    let faqEl = document.getElementById('faq-jsonld') as HTMLScriptElement;
    if (faq && faq.length > 0) {
      if (!faqEl) {
        faqEl = document.createElement('script');
        faqEl.id = 'faq-jsonld';
        faqEl.type = 'application/ld+json';
        document.head.appendChild(faqEl);
      }
      faqEl.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faq.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer,
          },
        })),
      });
    }

    return () => {
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
      const bc = document.getElementById('breadcrumb-jsonld');
      if (bc) bc.remove();
      const fqEl = document.getElementById('faq-jsonld');
      if (fqEl) fqEl.remove();
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogType, jsonLd, hreflangs, breadcrumbs, faq]);
}
